import type { Level } from "../../domain/learning/model/level";
import type { LevelSummary } from "../../domain/learning/model/level-summary";
import type { LevelStory } from "../../domain/story/model/level-story";
import { toLevel, toLevelStory, toManifest } from "./mapper";
import { ContentSchema } from "./schema";

/** 教材JSONの取得に失敗したことを表す（スキーマ不正によるZodErrorとは区別する）。 */
export class ContentLoadError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "ContentLoadError";
  }
}

const DEFAULT_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 200;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 指数バックオフで最大`retries`回リトライしてJSONを取得する（エラーハンドリング方針）。 */
async function fetchJsonWithRetry(
  url: string,
  fetchImpl: typeof fetch,
  retries: number,
  baseDelayMs: number,
): Promise<unknown> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetchImpl(url);
      if (!response.ok) {
        throw new ContentLoadError(
          `教材を読み込めませんでした: ${url} (status ${response.status})`,
        );
      }
      return await response.json();
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await sleep(baseDelayMs * 2 ** attempt);
      }
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new ContentLoadError(`教材を読み込めませんでした: ${url}`);
}

export interface ContentLoaderOptions {
  /** テスト用に差し替え可能。既定はグローバルの`fetch`。 */
  readonly fetchImpl?: typeof fetch;
  readonly retries?: number;
  readonly baseDelayMs?: number;
}

/**
 * 教材JSON（`public/data/`）のフェッチャ。取得 → Zod検証 → ドメイン値オブジェクトへの
 * 変換までを担う。取得失敗は指数バックオフで3回リトライし、スキーマ不正は
 * `ZodError` としてそのまま呼び出し元へ伝播させる（当該レベルのみ起動不可にする判断は
 * アプリケーション層が行う）。
 */
export class ContentLoader {
  private readonly fetchImpl: typeof fetch;
  private readonly retries: number;
  private readonly baseDelayMs: number;
  /** 同一URLの取得＋パースを共有する（門の先読みとS-2入場で二重にZodしない）。 */
  private readonly levelCache = new Map<string, Promise<Level>>();
  private readonly storyCache = new Map<string, Promise<LevelStory>>();
  private manifestCache: Promise<readonly LevelSummary[]> | null = null;

  constructor(options: ContentLoaderOptions = {}) {
    // 既定は毎回 globalThis.fetch を見に行く。モジュール読込時点の fetch 参照を
    // 閉じ込めると、テストの stubGlobal や実行時ポリフィルと食い違うため。
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
    this.retries = options.retries ?? DEFAULT_RETRIES;
    this.baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  }

  async loadManifest(): Promise<readonly LevelSummary[]> {
    if (this.manifestCache) {
      return this.manifestCache;
    }
    const pending = this.fetchJson("/data/manifest.json")
      .then((raw) => toManifest(ContentSchema.manifest.parse(raw)))
      .catch((error: unknown) => {
        this.manifestCache = null;
        throw error;
      });
    this.manifestCache = pending;
    return pending;
  }

  async loadLevel(levelId: string): Promise<Level> {
    const cached = this.levelCache.get(levelId);
    if (cached) {
      return cached;
    }
    const pending = this.fetchJson(`/data/levels/${levelId}.json`)
      .then((raw) => toLevel(ContentSchema.levelFile.parse(raw)))
      .catch((error: unknown) => {
        this.levelCache.delete(levelId);
        throw error;
      });
    this.levelCache.set(levelId, pending);
    return pending;
  }

  async loadLevelStory(levelId: string): Promise<LevelStory> {
    const cached = this.storyCache.get(levelId);
    if (cached) {
      return cached;
    }
    const pending = this.fetchJson(`/data/stories/${levelId}.json`)
      .then((raw) => toLevelStory(ContentSchema.storyFile.parse(raw)))
      .catch((error: unknown) => {
        this.storyCache.delete(levelId);
        throw error;
      });
    this.storyCache.set(levelId, pending);
    return pending;
  }

  private fetchJson(url: string): Promise<unknown> {
    return fetchJsonWithRetry(url, this.fetchImpl, this.retries, this.baseDelayMs);
  }

  /** テスト専用。本番コードからは呼ばない。 */
  clearCacheForTests(): void {
    this.levelCache.clear();
    this.storyCache.clear();
    this.manifestCache = null;
  }
}
